/**
 * @fileoverview Own draft editing and explicit persistence of a brief-filed activity. The backend owns saved matter/activity identity; the parent refreshes the accepted timeline. Venue, links, and tags are evidence attached to the filed-brief record rather than a separate filing or document-submission workflow.
 */

import React, { useContext, useEffect, useState } from 'react';
import api from '../../../api';
import { AppButton, AppModal, AppTextarea, AppAutoComplete, AppTooltip, AppDropdown, AppCalendar } from '../../shared';
import ToastContext from '../../../ToastContext';
import AppIcon from "../../shared/AppIcon";



function AddBriefFiledForm({ briefFiledForm, setBriefFiledForm, matterNumber, fetchActivities }) {
  const showToast = useContext(ToastContext);
  const [allTags, setAllTags] = useState([]);

  const [formContent, setFormContent] = useState({
    date: new Date(),
    location: '',
    link: '',
    tags: []
  });

  useEffect(() => {
    api.get('tags/BriefFiled')
    .then((response) => {
      console.log(response.data);
      setAllTags(response.data.tags);
    })
    .catch((error) => {
      console.error(error);
    });
  }, []);

  useEffect(() => {
    if (briefFiledForm.isEditing && briefFiledForm.currentBriefFiled) {
      console.log(briefFiledForm.currentBriefFiled);
      setFormContent({
        date: new Date(briefFiledForm.currentBriefFiled.Date),
        location: briefFiledForm.currentBriefFiled.Location,
        link: briefFiledForm.currentBriefFiled.Link,
        tags: briefFiledForm.currentBriefFiled.Tags
      });
    } else {
      setFormContent({
        date: new Date(),
        location: '',
        link: '',
        tags: []
      });
    }
  }, [briefFiledForm]);

  const submit =

  () => {
    const payload = {
      ActivityType: 'BriefFiled',
      Date: formContent.date,
      Location: formContent.location,
      Link: formContent.link,
      Tags: formContent.tags,
      MatterNumber: matterNumber,
      id: briefFiledForm.currentBriefFiled.id
    };

    console.log('payload:', payload);

    const apiMethod = briefFiledForm.isEditing ? api.put : api.post;
    const endpoint = briefFiledForm.isEditing ? `/activity/${briefFiledForm.currentBriefFiledId}` : `/activity`;

    apiMethod(endpoint, payload)
    .then((response) => {
      fetchActivities();
      showToast({
        severity: 'success',
        summary: 'Successful',
        detail: briefFiledForm.isEditing ? 'Brief Filed updated' : 'Brief Filed info added to activity log'
      });
      setBriefFiledForm({
        ...briefFiledForm,
        visible: false,
        isEditing: false,
        currentBriefFiled: null
      });
    })
    .catch((error) => {
      console.error('Error:', error);
      showToast({ severity: 'error', summary: 'Error', detail: error.message });
    });
  };

  const deleteBriefFiled =

  () => {
    api.delete('/activity', { data: { id: briefFiledForm.currentBriefFiledId } })
    .then((response) => {
      if (response.status === 201) {// Added status check
        fetchActivities();
        showToast({
          severity: 'success',
          summary: 'Successful',
          detail: 'Brief Filed deleted'
        });
        setBriefFiledForm({
          ...briefFiledForm,
          visible: false,
          isEditing: false,
          currentBriefFiled: null
        });
      } else {
        showToast({ severity: 'error', summary: 'Error', detail: 'Unexpected status code' });
      }
    })
    .catch((error) => {
      console.error('Error:', error);
      showToast({ severity: 'error', summary: 'Error', detail: error.message });
    });
  };

  const footerContent =
  <div className="case-details-modal__actions">
            {briefFiledForm.isEditing && <AppButton label="Delete" icon="pi pi-trash" variant="danger" onClick={deleteBriefFiled} />}
            <AppButton label={briefFiledForm.isEditing ? 'Update' : 'Submit'} icon="pi pi-check" onClick={submit} autoFocus />
        </div>;


  return (
    <>
            <AppModal
        title={briefFiledForm.isEditing ? 'Edit Brief Filed' : 'Brief Filed'}
        description="Capture when and where a brief was filed, along with any supporting links or tags."
        visible={briefFiledForm.visible}
        width="min(92vw, 760px)"
        onClose={() => setBriefFiledForm((prev) => ({ ...prev, visible: false }))}
        footer={footerContent}
        className="case-details-modal">

                <div className="case-details-modal__form">
                    <section className="case-details-modal__section">
                        <div className="case-details-modal__section-header">
                            <div>
                                <h3 className="case-details-modal__section-title">Filing Details</h3>
                                <p className="case-details-modal__section-description">
                                    Record the date and venue for the filed brief so the timeline stays complete.
                                </p>
                            </div>
                        </div>
                        <div className="case-details-modal__section-body">
                            <div className="case-details-modal__grid">
                                <div className="case-details-modal__field">
                                    <label htmlFor="date" className="case-details-modal__label">Date</label>
                                    <AppCalendar
                    value={formContent.date}
                    onChange={(e) => setFormContent((prev) => ({ ...prev, date: e.value }))} />

                                </div>
                                <div className="case-details-modal__field">
                                    <label htmlFor="location" className="case-details-modal__label">Location</label>
                                    <AppDropdown
                    id="location"
                    value={formContent.location}
                    options={[
                    { name: 'Federal Court' },
                    { name: 'State Court' },
                    { name: 'Arbitration' }]}

                    onChange={(e) => setFormContent((prev) => ({ ...prev, location: e.value }))}
                    optionLabel="name"
                    optionValue="name"
                    placeholder="Select a location" />

                                </div>
                            </div>
                        </div>
                    </section>

                    <section className="case-details-modal__section">
                        <div className="case-details-modal__section-header">
                            <div>
                                <h3 className="case-details-modal__section-title">Supporting Links</h3>
                                <p className="case-details-modal__section-description">
                                    Store any docket or filing links that should be easy to reopen later.
                                </p>
                            </div>
                        </div>
                        <div className="case-details-modal__section-body">
                            <div className="case-details-modal__field">
                                <label htmlFor="link" className="case-details-modal__label">
                                    Links
                                    <AppTooltip content="Add multiple links by putting each one on a new line">
                                        <AppIcon icon="pi pi-question-circle case-details-modal__inline-icon" aria-hidden="true" />
                                    </AppTooltip>
                                </label>
                                <AppTextarea
                  id="link"
                  value={formContent.link}
                  rows={4}
                  onChange={(val) => setFormContent((prev) => ({ ...prev, link: val }))} />

                                <div className="case-details-modal__helper">Separate multiple links by placing each one on its own line.</div>
                            </div>
                        </div>
                    </section>

                    <section className="case-details-modal__section">
                        <div className="case-details-modal__section-header">
                            <div>
                                <h3 className="case-details-modal__section-title">Tags</h3>
                                <p className="case-details-modal__section-description">
                                    Apply tags that make this filing easier to scan and filter from the activity tab.
                                </p>
                            </div>
                        </div>
                        <div className="case-details-modal__section-body">
                            <div className="case-details-modal__field">
                                <label htmlFor="tags" className="case-details-modal__label">Tags</label>
                                <BriefTagSelect
                  selectedTags={formContent.tags}
                  setSelectedTags={(tags) => setFormContent((prev) => ({ ...prev, tags }))}
                  allTags={allTags} />

                            </div>
                        </div>
                    </section>
                </div>
            </AppModal>
        </>);

}



function BriefTagSelect({ selectedTags, setSelectedTags, allTags }) {
  const [filteredTags, setFilteredTags] = useState([]);
  const [query, setQuery] = useState('');
  const [autoCompleteKey, setAutoCompleteKey] = useState(0);

  const search =

  (event) => {
    setQuery(event.query);
    if (allTags.length === 0) {
      setFilteredTags([]);
      return;
    }
    setTimeout(() => {
      let _filteredTags;
      if (!query.trim().length) {
        _filteredTags = [...allTags];
      } else {
        _filteredTags = allTags.filter((tag) => {
          return tag.toLowerCase().startsWith(query.toLowerCase());
        });
      }
      setFilteredTags(_filteredTags);
    });
  };

  const handleKeyPress =

  (e) => {
    if (e.key === 'Enter' && query.length > 0) {
      // const newItem = {
      //     TagType: 'BriefFiled',
      //     Name: query
      // }
      setSelectedTags([...selectedTags, query]);
      setQuery('');
      setAutoCompleteKey(autoCompleteKey + 1);
    }
  };

  useEffect(() => {
    console.log('allTags:', allTags);
  }, [allTags]);

  useEffect(() => {
    console.log('selectedTags:', selectedTags);
  }, [selectedTags]);

  useEffect(() => {
    console.log('filteredTags:', filteredTags);
  }, [filteredTags]);

  useEffect(() => {
    console.log('query:', query);
  }, [query]);

  return (
    <AppAutoComplete
      value={selectedTags}
      suggestions={filteredTags}
      completeMethod={search}
      multiple
      onChange={(e) => setSelectedTags(e.value)}
      onKeyPress={handleKeyPress}
      className="w-full"
      dropdown
      placeholder="Add tags"
      key={autoCompleteKey} />);


}

export default AddBriefFiledForm;
